/** /me — a student's own mastery across every course, per objective, with the bands spelled
 * out. Nothing about other students appears here. */
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { get, post } from "../api/client";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { Button, DraftChip, ExternalLink, OptionCard, SaveNotice, useConfirm, useFileSave } from "../ui";
import { useSession } from "../auth/SessionContext";
import { capabilities } from "../platform/capabilities";
import { nsKey } from "../platform/storage";
import { product } from "../product";
import { ConnectCard, telegramCall, type TelegramStatus } from "../telegram/ConnectCard";

type Cell = { lo_id: string; code: string; text: string; score: number; band: string; n: number; last_assessed_at: string | null };
type Mine = { subject: string; courses: { course_id: string; title: string; respondent_id: string; release_mode: string; released_sessions: number; los: Cell[] }[] };
type HwItem = { item_id: string; lo_id: string | null; snapshot: { type: string; stem: string; options?: { key: string; label?: string; image?: string | null }[]; lang?: string } };
type HwAnswer = { answer: unknown; correct: boolean | null; answered_at: string };
type MyAssignment = { assignment_id: string; course_id: string; course_title: string; title: string; due_date: string | null; released_at: string; completed_at: string | null; los: { lo_id: string; code: string; text: string }[]; items: HwItem[]; answers: Record<string, HwAnswer>; readings: { reading_id: string; lo_id: string | null; title: string; author: string; kind: string; url: string; note: string }[] };

const glyph: Record<string, string> = { secure: "■", developing: "▦", not_yet: "□", unassessed: "·" };
const tone: Record<string, string> = { secure: "var(--secure)", developing: "var(--developing)", not_yet: "var(--not-yet)", unassessed: "var(--ink-2)" };

export function Me() {
  const { t, i18n } = useTranslation();
  const confirm = useConfirm();
  const { principal, signOut } = useSession();
  const mine = useQuery({ queryKey: ["me", "mastery"], queryFn: () => get<Mine>("/analytics/v1/me/mastery"), enabled: !!principal });
  // STORY-082: the last homework and readings stay readable offline
  const homework = useQuery({
    queryKey: ["me", "assignments"],
    queryFn: async () => {
      const data = await get<MyAssignment[]>("/analytics/v1/me/assignments");
      localStorage.setItem(nsKey("me_assignments_cache"), JSON.stringify(data));
      return data;
    },
    placeholderData: () => {
      try {
        const raw = localStorage.getItem(nsKey("me_assignments_cache"));
        return raw ? (JSON.parse(raw) as MyAssignment[]) : undefined;
      } catch {
        return undefined;
      }
    },
    enabled: !!principal,
  });
  const offlineHomework = homework.isError && !!homework.data;
  const fmt = (iso: string | null) => (iso ? new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium" }).format(new Date(iso)) : "");
  const [note, setNote] = useState<string | null>(null);
  const [bioLock, setBioLock] = useState(localStorage.getItem(nsKey("bio_lock")) === "1");
  const [pushNote, setPushNote] = useState<string | null>(null);
  const file = useFileSave();
  const exportMe = useMutation({
    mutationFn: () => get<Record<string, unknown>>("/analytics/v1/me/export"),
    onSuccess: async (data) => {
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const saved = await file.save("my-data.json", blob);
      // `SaveNotice` names the path where the app writes the file itself; in a browser the
      // download list is the receipt and this line is the only acknowledgement there is.
      setNote(saved.kind === "browser" ? t("me.exported") : null);
    },
  });
  const eraseMe = useMutation({
    mutationFn: async () => {
      await post<unknown>("/analytics/v1/me/erase");
      await post<unknown>("/session/v1/me/erase");
      await post<unknown>("/curriculum/v1/me/erase");
    },
    onSuccess: () => {
      setNote(t("me.erased"));
      void mine.refetch();
    },
  });

  return (
    <main className="font-system mx-auto flex min-h-dvh max-w-2xl flex-col gap-6 p-6">
      <header className="flex items-center justify-between">
        <Link to="/" className="text-lg font-bold tracking-tight" translate="no">{product.name}</Link>
        {principal && <button type="button" className="min-h-[var(--control-h)] px-2 text-sm underline" onClick={signOut}>{t("app.signOut")}</button>}
      </header>
      <div>
        <h1 className="text-2xl font-bold">{t("me.title")}</h1>
        <p className="text-[color:var(--ink-2)]">{t("me.lede")}</p>
      </div>
      {!principal && (
        <p className="rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 text-sm">
          {t("me.signedOut")} <Link to="/signin" className="underline">{t("signin.cta")}</Link>
        </p>
      )}
      {mine.isPending && principal && <p>{t("app.loading")}</p>}
      {mine.data && mine.data.courses.length === 0 && (
        <p className="rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 text-sm">{t("me.empty")}</p>
      )}
      {homework.data && homework.data.length > 0 && (
        <section className="rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 shadow-sm" aria-labelledby="me-homework">
          <h2 id="me-homework" className="text-lg font-semibold">{t("me.hw.title")}</h2>
          <p className="mb-3 text-sm text-[color:var(--ink-2)]">{t("me.hw.lede")}</p>
          {offlineHomework && <p className="mb-3 text-xs text-[color:var(--ink-2)]" role="status">{t("me.hw.offline")}</p>}
          <ul className="flex flex-col gap-4">
            {homework.data.map((a) => (
              <HomeworkCard key={a.assignment_id} a={a} onAnswered={() => { void homework.refetch(); void mine.refetch(); }} />
            ))}
          </ul>
        </section>
      )}
      {mine.data?.courses.map((c) => {
        const bands = { secure: 0, developing: 0, not_yet: 0, unassessed: 0 };
        for (const lo of c.los) bands[lo.band as keyof typeof bands] += 1;
        return (
          <section key={c.course_id} className="rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 shadow-sm">
            <h2 className="text-lg font-semibold">{c.title || t("me.untitled")}</h2>
            <p className="mb-3 text-sm text-[color:var(--ink-2)]">
              {t("me.summary", { secure: bands.secure, developing: bands.developing, notYet: bands.not_yet, total: c.los.length })}
              {c.release_mode === "per_session" && ` · ${t("me.released", { count: c.released_sessions })}`}
            </p>
            <ul className="flex flex-col gap-2">
              {c.los.map((lo) => (
                <li key={lo.lo_id} className="flex items-start gap-3">
                  <span aria-hidden className="w-4 text-lg leading-6" style={{ color: tone[lo.band] }}>{glyph[lo.band]}</span>
                  <div className="min-w-0 flex-1">
                    <p className="break-words"><span className="mr-2 font-mono text-xs text-[color:var(--ink-2)]">{lo.code}</span>{lo.text}</p>
                    <p className="text-xs text-[color:var(--ink-2)]">
                      {t(`me.bands.${lo.band}`)}
                      {lo.n > 0 && ` · ${t("me.evidence", { count: lo.n })}`}
                      {lo.last_assessed_at && ` · ${fmt(lo.last_assessed_at)}`}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        );
      })}
      <p className="text-xs text-[color:var(--ink-2)]">{t("me.legend")}</p>
      {principal && <TelegramSection />}
      {principal && (capabilities.biometric || capabilities.push) && (
        <section className="rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 text-sm shadow-sm">
          <h2 className="mb-1 font-semibold">{t("me.device")}</h2>
          {capabilities.biometric && (
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={bioLock} onChange={async (e) => { if (e.target.checked && !(await capabilities.biometric!.authenticate(t("me.bioReason")))) return; localStorage.setItem(nsKey("bio_lock"), e.target.checked ? "1" : "0"); setBioLock(e.target.checked); }} />
              {t("me.bioLock")}
            </label>
          )}
          {capabilities.push && (
            <p className="mt-2 flex flex-wrap items-center gap-2">
              <Button variant="secondary" onClick={async () => setPushNote((await capabilities.push!.register()) ? t("me.pushOk") : t("me.pushNo"))}>{t("me.push")}</Button>
              {pushNote && <span>{pushNote}</span>}
            </p>
          )}
        </section>
      )}
      {principal && (
        <section className="rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 text-sm shadow-sm">
          <h2 className="mb-1 font-semibold">{t("me.rights")}</h2>
          <p className="mb-3 text-[color:var(--ink-2)]">{t("me.rightsBody")}</p>
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" loading={exportMe.isPending} onClick={() => exportMe.mutate()}>{t("me.export")}</Button>
            <Button
              variant="destructive"
              loading={eraseMe.isPending}
              onClick={async () => {
                if (await confirm({ title: t("me.eraseTitle"), body: t("me.eraseConfirm"), confirmLabel: t("me.erase"), destructive: true })) eraseMe.mutate();
              }}
            >
              {t("me.erase")}
            </Button>
          </div>
          {note && <p className="mt-2" role="status">{note}</p>}
          <div className="mt-2"><SaveNotice result={file.result} /></div>
          {(exportMe.error || eraseMe.error) && <p className="mt-2" role="alert" style={{ color: "var(--danger)" }}>{t("me.rightsFailed")}</p>}
        </section>
      )}
    </main>
  );
}


function HomeworkCard({ a, onAnswered }: { a: MyAssignment; onAnswered: () => void }) {
  const { t, i18n } = useTranslation();
  const [text, setText] = useState<Record<string, string>>({});
  const [multi, setMulti] = useState<Record<string, string[]>>({});
  const [err, setErr] = useState<string | null>(null);
  const send = useMutation({
    mutationFn: ({ item_id, answer }: { item_id: string; answer: unknown }) => post<{ item_id: string; correct: boolean | null; completed: boolean }>(`/analytics/v1/me/assignments/${a.assignment_id}/answers`, { item_id, answer }),
    onSuccess: () => { setErr(null); onAnswered(); },
    onError: (e) => setErr(String(e)),
  });
  const fmt = (iso: string | null) => (iso ? new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium" }).format(new Date(iso)) : "");
  const done = Object.keys(a.answers).length;
  return (
    <li className="rounded-[var(--radius-card)] border p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="font-semibold">{a.title || t("me.hw.untitled")} <span className="text-sm font-normal text-[color:var(--ink-2)]">· {a.course_title}</span></h3>
        <p className="text-xs text-[color:var(--ink-2)]">
          {a.completed_at ? t("me.hw.completed", { date: fmt(a.completed_at) }) : t("me.hw.progress", { done, total: a.items.length })}
          {a.due_date ? ` · ${t("me.hw.due", { date: fmt(a.due_date) })}` : ""}
        </p>
      </div>
      <p className="mb-2 text-xs text-[color:var(--ink-2)]">{t("me.hw.on")} {a.los.map((lo) => lo.code).join(", ")}</p>
      <ol className="flex flex-col gap-3">
        {a.items.map((it, i) => {
          const type = it.snapshot.type;
          const options = it.snapshot.options ?? [];
          const got = a.answers[it.item_id];
          const tone = got ? (got.correct === true ? "var(--secure)" : got.correct === false ? "var(--not-yet)" : "var(--accent)") : "transparent";
          const stateOf = (key: string) => {
            if (got) {
              const chosen = Array.isArray(got.answer) ? (got.answer as string[]).includes(key) : got.answer === key;
              return chosen ? (got.correct === true ? "correct" : got.correct === false ? "incorrect" : "submitted") : "default";
            }
            return (multi[it.item_id] ?? []).includes(key) ? "selected" : "default";
          };
          return (
            <li key={it.item_id} className="rounded-[var(--radius-control)] p-2" style={{ outline: got ? `2px solid ${tone}` : "none" }}>
              <p className="mb-2"><span className="mr-2 text-xs text-[color:var(--ink-2)]">{i + 1}.</span>{it.snapshot.stem}</p>
              {(type === "single" || type === "multi") && (
                <div role={type === "multi" ? "group" : "radiogroup"} aria-label={t("me.hw.options")} className="grid gap-2">
                  {options.map((o) => (
                    <OptionCard key={o.key} label={o.key} multi={type === "multi"} state={stateOf(o.key)} bare={!o.label} onSelect={() => {
                      if (type === "multi") {
                        const cur = multi[it.item_id] ?? [];
                        setMulti({ ...multi, [it.item_id]: cur.includes(o.key) ? cur.filter((k) => k !== o.key) : [...cur, o.key] });
                      } else send.mutate({ item_id: it.item_id, answer: o.key });
                    }}>{o.label}</OptionCard>
                  ))}
                  {type === "multi" && <Button variant="secondary" disabled={!(multi[it.item_id] ?? []).length} loading={send.isPending} onClick={() => send.mutate({ item_id: it.item_id, answer: multi[it.item_id] ?? [] })}>{t("me.hw.send")}</Button>}
                </div>
              )}
              {(type === "numeric" || type === "short_text") && (
                <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); const v = text[it.item_id] ?? ""; if (v) send.mutate({ item_id: it.item_id, answer: type === "numeric" ? Number(v) : v }); }}>
                  <input className="field min-h-[var(--control-h)] flex-1" aria-label={t("me.hw.answerField")} inputMode={type === "numeric" ? "decimal" : "text"} maxLength={200} value={text[it.item_id] ?? (got ? String(got.answer) : "")} onChange={(e) => setText({ ...text, [it.item_id]: e.target.value })} />
                  <Button type="submit" loading={send.isPending}>{t("me.hw.send")}</Button>
                </form>
              )}
              {got && (
                <p className="mt-1 text-sm" role="status">
                  {got.correct === true ? t("me.hw.right") : got.correct === false ? t("me.hw.wrong") : t("me.hw.received")}
                </p>
              )}
            </li>
          );
        })}
      </ol>
      {a.readings.length > 0 && (
        <div className="mt-3 text-sm">
          <p className="font-medium">{t("me.hw.readings")}</p>
          <ul className="flex flex-col gap-1">
            {a.readings.map((r) => (
              <li key={r.reading_id}>
                {r.url ? <ExternalLink href={r.url} className="underline">{r.title}</ExternalLink> : r.title}
                {r.author && <span className="text-[color:var(--ink-2)]"> · {r.author}</span>}
                {r.note && <span className="text-xs text-[color:var(--ink-2)]"> — {r.note}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
      {err && <p role="alert" className="mt-2 text-sm" style={{ color: "var(--danger)" }}>{err}</p>}
      {!a.completed_at && <p className="mt-2 text-xs text-[color:var(--ink-2)]"><DraftChip kind="edited" /> {t("me.hw.weight")}</p>}
    </li>
  );
}


/** STORY-096: Telegram for join links and report notices; STORY-122: the shared connect card
 * shows the disclosure before a code is asked for, and stays the one place the wording lives
 * (the course setting links here, `#me-telegram`). The preferences are this page's, rendered
 * inside the card once a chat is linked; ending the link is the card's one confirmed control
 * (STORY-127), so there is no second, quieter way around it here. */
function TelegramSection() {
  const { t } = useTranslation();
  const prefs = useMutation({ mutationFn: (body: { notify_join: boolean; notify_reports: boolean }) => telegramCall<TelegramStatus>("PUT", "/integrations/v1/me/telegram", null, body) });
  return (
    <ConnectCard
      id="me-telegram"
      title={t("me.tg.title")}
      lede={t("me.tg.body")}
      linked={(s, refetch) => (
        <>
          <label className="flex min-h-[var(--control-h)] items-center gap-2"><input type="checkbox" className="h-[1.125em] w-[1.125em]" checked={s.notify_join} onChange={(e) => prefs.mutate({ notify_join: e.target.checked, notify_reports: s.notify_reports }, { onSuccess: refetch })} />{t("me.tg.joinLinks")}</label>
          <label className="flex min-h-[var(--control-h)] items-center gap-2"><input type="checkbox" className="h-[1.125em] w-[1.125em]" checked={s.notify_reports} onChange={(e) => prefs.mutate({ notify_join: s.notify_join, notify_reports: e.target.checked }, { onSuccess: refetch })} />{t("me.tg.reports")}</label>
        </>
      )}
    />
  );
}
